import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../../components/CustomInputLabel';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { useModel } from '../../../../../../../hooks/useModel';
import { useRouter } from '../../../../../../../hooks/useRouter';
import { useSnackbar } from '../../../../../../../hooks/useSnackbar';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import {
  batchDeleteDocs,
  removeDoc,
  updateDataInDoc,
  updateDoc
} from '../../../../../../../services/firebase';
import { OrgSelector } from '../../../../../../components/OrgSelector';
import { SafeExecuteButton } from '../../../../../../components/SafeExecuteButton';
import {
  copyExperienceTemplateToOtherOrg,
  getExperienceIntancesByTemplateId
} from '../../../../../../services/experiences';

const ButtonContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',

  '> :not(:first-of-type)': {
    marginLeft: p.theme.spacing()
  }
}));

const ErrorContainer = styled('div')({
  marginTop: 16,
  padding: 8,
  border: '1px solid #d32f2f',
  borderRadius: 4
});

const ErrorHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer'
});

const ErrorDetails = styled('div')({
  marginTop: 8,
  maxHeight: 200,
  overflow: 'auto'
});

const EmployeeAppVersionSwitcher = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const [m, setM] = useModel(tpl);
  return (
    <FlexContainerVertical>
      <FlexContainer>
        <Typography variant="h5">Employee App Version</Typography>
      </FlexContainer>

      <FlexContainer fullWidth>
        <Select
          fullWidth
          value={m.data.employeeApp.v}
          onChange={(ev) =>
            setM((x) =>
              updateDataInDoc(x, {
                employeeApp: {
                  ...x.data.employeeApp,
                  v: +(ev.target.value as string) as 1 | 1.5 | 2
                }
              })
            )
          }
        >
          <MenuItem key={1} value={1}>
            V1 - Old version (no traces of anything new)
          </MenuItem>
          <MenuItem key={1.5} value={1.5}>
            V2 - Pre-rollout (employees see old version, but new version
            accessible via special url and Experience Hub step shown in Tpl
            builder)
          </MenuItem>
          <MenuItem key={2} value={2}>
            V2 - New version fully rolled out
          </MenuItem>
        </Select>

        <ButtonWithPromise
          pending="Saving..."
          variant="contained"
          color="primary"
          disabled={m.data.employeeApp.v === tpl.data.employeeApp.v}
          onClick={() => updateDoc(m, (x) => ({ employeeApp: x.employeeApp }))}
        >
          Save version
        </ButtonWithPromise>
      </FlexContainer>
    </FlexContainerVertical>
  );
};

const CopyToOtherOrgDialog = ({
  open,
  onClose,
  tpl
}: {
  open: boolean;
  onClose: () => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const [org, setOrg] = useState<Doc<IOrganization> | null>(null);
  const [errorData, setErrorData] = useState<{
    message: string;
    fields: string[];
    showDetails: boolean;
    objName: string;
  } | null>(null);
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <CustomInputLabel label="Choose an organization">
          <OrgSelector value={org} onChange={setOrg} />
        </CustomInputLabel>

        {errorData && (
          <ErrorContainer>
            <ErrorHeader
              onClick={() =>
                setErrorData({
                  ...errorData,
                  showDetails: !errorData.showDetails
                })
              }
            >
              <Typography color="error">{errorData.message}</Typography>
              <Typography>
                {errorData.showDetails ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </Typography>
            </ErrorHeader>

            {errorData.showDetails && (
              <ErrorDetails>
                {errorData.fields.map((field, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    style={{ fontFamily: 'monospace' }}
                  >
                    {field}
                  </Typography>
                ))}
              </ErrorDetails>
            )}

            <Typography style={{ marginTop: 16 }}>
              Do you want to set the undefined fields to null and proceed with
              the copying? Selecting this will not change the original template,
              but this may produce unintended consequences in the new template.
            </Typography>
          </ErrorContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          disabled={!org}
          pending="Copying..."
          variant="contained"
          color="primary"
          onClick={async () => {
            if (!org) return;

            try {
              await copyExperienceTemplateToOtherOrg(
                tpl,
                org.id,
                org.data.tz,
                !!errorData
              );
              enqueueSnackbarSuccess('Template copied!');
              setErrorData(null);
              onClose();
            } catch (err: any) {
              if (err.undefinedFields) {
                const fieldList =
                  err.undefinedFields?.map((field: any) => field.fullPath) ||
                  [];
                const objName = err.objName || 'Document';

                setErrorData({
                  message: err.message,
                  fields: fieldList.length
                    ? fieldList
                    : ['Field information not available'],
                  showDetails: false,
                  objName
                });
              } else {
                const message = err?.message
                  ? `Failed to copy template: ${err.message}`
                  : 'Failed to copy template';
                enqueueSnackbarError(message);
                console.log('Template copy error: ', err);
              }
            }
          }}
        >
          {errorData ? 'Fix and Copy' : 'Copy'}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

export const Actions = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const { history } = useRouter();
  const copyDialog = useDialogState();
  return (
    <Section>
      <CanvasBar>Actions</CanvasBar>
      <Card>
        <ButtonContainer>
          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={async (execute) => {
              console.log('About to remove, keeping instances', tpl);
              if (execute) {
                await removeDoc(tpl);
                history.push(`/orgs/${tpl.data.orgId}`);
              }
            }}
          >
            Remove, but keep instances
          </SafeExecuteButton>

          <SafeExecuteButton
            variant="contained"
            color="secondary"
            onClick={async (execute) => {
              const instances = await getExperienceIntancesByTemplateId(tpl.id);
              console.log('About to remove, including instances', {
                tpl,
                instances
              });
              if (execute) {
                await batchDeleteDocs(instances);
                await removeDoc(tpl);
                history.push(`/orgs/${tpl.data.orgId}`);
              }
            }}
          >
            Remove, including all instances
          </SafeExecuteButton>

          <Button
            variant="contained"
            color="primary"
            onClick={copyDialog.openDialog}
          >
            Copy template to other org...
          </Button>
          <CopyToOtherOrgDialog
            open={copyDialog.dialogOpen}
            onClose={copyDialog.closeDialog}
            tpl={tpl}
          />
        </ButtonContainer>
      </Card>
      <br />
      <Card>
        <EmployeeAppVersionSwitcher tpl={tpl} />
      </Card>
    </Section>
  );
};
